import { isEssayInstructionBlob } from './essayChoiceGroup';

function promptText(option) {
  return String(option?.text || option?.question_text || option?.prompt_text || option?.question || '').trim();
}

function rowExamBlob(row) {
  return [
    row?.parameter,
    row?.external_question_id,
    row?.external_id,
    row?.paper_id,
    row?.exam_name,
    row?.id,
  ].map((v) => String(v || '')).join(' ');
}

function isNonLnatRow(row) {
  const blob = rowExamBlob(row);
  return /\b(TSA|TARA|UCAT|TMUA|BMAT|ESAT|LSAT)\b/i.test(blob) && !/\bLNAT\b/i.test(blob);
}

function isLnatEssayGroup(row) {
  if (!row || isNonLnatRow(row)) return false;
  return row.interaction_kind === 'essay_choice_group'
    || row.is_essay === true
    || (Array.isArray(row.essay_options) && row.essay_options.length > 0);
}

function realPromptEntries(row) {
  const options = Array.isArray(row?.essay_options) ? row.essay_options : [];
  const fromOptions = options
    .map((option) => ({ option, text: promptText(option) }))
    .filter(({ text }) => text && !isEssayInstructionBlob(text));
  if (fromOptions.length) return fromOptions;
  const question = String(row?.question || '').trim();
  if (question && !isEssayInstructionBlob(question)) {
    return [{ option: { text: question }, text: question }];
  }
  return [];
}

function alreadyOneNonBlobPrompt(row) {
  return realPromptEntries(row).length === 1;
}

/**
 * Expand each LNAT essay_choice_group to N items (real essay_options texts).
 * Non-LNAT rows (MCQ / TSA) are a no-op.
 * Skip double-expand only when every row already has exactly one non-blob prompt.
 */
export function expandLnatSectionBQuestions(questions) {
  const list = Array.isArray(questions) ? questions : [];
  if (!list.length) return list;
  if (!list.some(isLnatEssayGroup)) return list;
  if (list.every(alreadyOneNonBlobPrompt)) return list;

  return list.flatMap((row) => {
    if (!isLnatEssayGroup(row)) return [row];
    const prompts = realPromptEntries(row);
    if (!prompts.length) return [];
    const exhibitIsBlob = isEssayInstructionBlob(row.exhibit_text);
    return prompts.map((entry, index) => {
      const prompt = entry.text;
      const option = entry.option || {};
      const label = option.label || String.fromCharCode(65 + (index % 26));
      return {
        ...row,
        id: `${row.id != null ? row.id : 'essay'}-prompt-${index + 1}`,
        question: prompt,
        exhibit_text: exhibitIsBlob ? null : row.exhibit_text,
        essay_options: [{ ...option, label, text: prompt }],
        interaction_kind: 'essay_choice_group',
        is_essay: true,
        multiple_choice: false,
        options: [],
      };
    });
  });
}

export function lnatSecondEssayBlocked(answers, sectionBQuestions, currentId) {
  return (sectionBQuestions || []).some((question) => {
    if (String(question?.id) === String(currentId)) return false;
    const text = answers?.[question.id]?.text_response;
    return Boolean(text && String(text).trim());
  });
}

/** Section B keeps Flag (harvest 14). Opposite of TARA Writing sample. */
export const LNAT_SECTION_B_SHOWS_FLAG = true;

export function sectionBShowsFlag() {
  return LNAT_SECTION_B_SHOWS_FLAG;
}

export function lnatPostExamRowStatus(question, userAnswers = {}, visitedQuestions = {}) {
  const isSectionB = Number(question?.tmuaPaper) === 2
    || question?.is_essay === true
    || question?.interaction_kind === 'essay_choice_group';
  const answered = Boolean(String(userAnswers[question?.id]?.text_response || '').trim())
    || Boolean(userAnswers[question?.id]?.label && question?.correct_answer
      && userAnswers[question.id].label === question.correct_answer);
  const visited = Boolean(visitedQuestions[question?.id]);

  if (isSectionB) {
    const hasText = Boolean(String(userAnswers[question?.id]?.text_response || '').trim());
    if (hasText) return { status: 'Complete', tone: 'complete' };
    if (visited) return { status: 'Incomplete', tone: 'incomplete' };
    return { status: 'Unseen', tone: 'unseen' };
  }

  if (!visited && !answered) return { status: 'Unseen', tone: 'unseen' };
  if (userAnswers[question?.id]?.label && userAnswers[question.id].label === question.correct_answer) {
    return { status: 'Correct', tone: 'correct' };
  }
  return { status: 'Incorrect', tone: 'incorrect' };
}

export function lnatSectionHasEssayText(sectionBQuestions, userAnswers = {}) {
  return (sectionBQuestions || []).some((question) => (
    Boolean(String(userAnswers[question?.id]?.text_response || '').trim())
  ));
}
